import { memo, useState, useCallback, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import Select from 'react-select';

import type {
    OptionTypeBase,
    GroupTypeBase,
    FocusEventHandler
} from 'react-select';

import './hb-dropdown.scss';

import HBDropdownIconOption from './HBDropdownIconOption';
import HBDropdownIconChevron from './HBDropdownIconChevron';
import HBLabel from '../HBLabel/HBLabel';

interface Props {
    label?: string;
    placeholder?: string;
    options: readonly (OptionTypeBase | GroupTypeBase<OptionTypeBase>)[];
    isValid?: boolean;
    errorMessage?: string;
    isRequired?: boolean;
    customClass?: string;
    name?: string;
    defaultValue?:
        | OptionTypeBase
        | readonly OptionTypeBase[]
        | null
        | undefined;
    onChange(
        selected: OptionTypeBase | readonly OptionTypeBase[] | null | undefined
    ): void;
    onBlur?: FocusEventHandler;
    // initialValue?: shape({}),
    isDisabled?: boolean;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    isSearchable?: boolean;
}

function HBDropdown({
    label,
    placeholder = 'Select an option',
    options,
    name,
    customClass,
    onChange,
    defaultValue = null,
    onBlur,
    errorMessage,
    isValid,
    isRequired,
    isDisabled = false,
    menuPlacement,
    isSearchable = true
}: Props): JSX.Element {
    const [selectedOption, setSelectedOption] = useState(defaultValue);
    const onSelectChange = useCallback(
        (selected: OptionTypeBase | readonly OptionTypeBase[] | null): void => {
            setSelectedOption(selected);
            onChange(selected);
        },
        [setSelectedOption, onChange]
    );

    const components = useMemo(
        () => ({
            Option: HBDropdownIconOption,
            DropdownIndicator: HBDropdownIconChevron
        }),
        []
    );

    return (
        <div
            className={`hbui-dropdown ${customClass} ${
                !isValid ? 'error' : ''
            }`}
        >
            <HBLabel
                label={label}
                isRequired={isRequired}
                customClass="dropdown-label"
                htmlFor={name}
                id={name}
            />
            <Select
                value={selectedOption}
                onChange={onSelectChange}
                onBlur={onBlur}
                options={options}
                name={name}
                placeholder={placeholder}
                className="hbui-dropdown-select"
                classNamePrefix="hbui-dropdown-select"
                components={components}
                menuPlacement={menuPlacement}
                isDisabled={isDisabled}
                isSearchable={isSearchable}
                // closeMenuOnSelect={false} // Note: Please don't delete. This is the way to keep the menu open for css updates.
                // defaultMenuIsOpen // Note: Please don't delete. This is the way to keep the menu open for css updates.
            />
            {!isValid && <div className="error-message">{errorMessage}</div>}
        </div>
    );
}

HBDropdown.propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            // TODO: correct shape?
        })
    ).isRequired,
    isValid: PropTypes.bool,
    errorMessage: PropTypes.string,
    isRequired: PropTypes.bool,
    customClass: PropTypes.string,
    name: PropTypes.string,
    defaultValue: PropTypes.shape({}),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    initialValue: PropTypes.shape({}),
    isDisabled: PropTypes.bool,
    menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
    isSearchable: PropTypes.bool
};

export default memo(HBDropdown);
